import React, { useState, useMemo } from "react";
import {
  Box,
  Grid,
  Stack,
  Button,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";

import {
  LineChart,
  AnnotatableChart,
} from "../../../../../shared/components/charts";
import { DashboardCard } from "../../../../../shared/components/DashboardCard";
import {
  FilterDrawer,
  FilterState,
  getAppliedFilterCount,
  DurationSelector,
  DateRange,
  CohortGuard,
  ComparisonToggle,
  ComparisonColumns,
  WeatherAnomalyCallout,
} from "../../../../../shared/components";
import { useSessionQuery } from "../../../../../features/auth/hooks";
import {
  useRecoveryTrends,
  useHeartRatePatterns,
} from "../hooks";
import { RecoveryStatsColumn } from "../components/RecoveryStatsColumn";
import {
  buildFilterParams,
  transformTrendsData,
  calculateGranularity,
} from "../../shared/utils";
import { useClientScope } from "../../../../../shared/contexts";
import {
  usePersistedDateRange,
  usePersistedFilters,
  useMetricsOverallRange,
  useMetricsStats,
  useComparisonMode,
  useComparisonGroups,
  useComparisonTrends,
  useComparisonPatterns,
} from "../../../../../shared/hooks";
import { AiSummaryPanel, useAiSummary } from "../../../insights";
import {
  buildComparisonTrendSeries,
  buildComparisonPatternSeries,
} from "../../../../../shared/utils";

const RECOVERY_DETAILS = {
  recoveryTrends: `Displays daily recovery metrics over time, illustrating changes in physiological readiness across the organisation. Recovery is typically derived from indicators such as heart rate variability (HRV), resting heart rate, and sleep quality, reflecting autonomic nervous system balance.

Tracking these trends helps identify sustained improvements, periods of reduced recovery, and potential fatigue accumulation, supporting proactive management of workload and wellbeing.`,
  recoveryScoreDistribution: `Displays the proportion of employees within each recovery range (e.g., low, moderate, high), providing a snapshot of overall workforce recovery status at a given time. Recovery scores reflect physiological readiness based on metrics such as HRV, resting heart rate, and sleep quality.

This distribution view helps identify how many employees may be well-recovered versus experiencing elevated fatigue or stress, supporting informed workload and wellbeing decisions.`,
  heartRatePatternsByTimeOfDay: `Displays how heart rate changes throughout the day, identifying peak and low periods across a 24-hour cycle. These fluctuations reflect variations in physical activity, metabolic demand, stress levels, and natural circadian rhythms.

Tracking these patterns helps distinguish expected daytime elevations from restful nighttime reductions, providing insight into daily workload, recovery periods, and overall cardiovascular regulation.`,
};

export const Recovery: React.FC = () => {
  const theme = useTheme();
  const { effectiveClientId, isCentralAdmin, isAnonymised } = useClientScope();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { filters: filterState, setFilters: setFilterState } =
    usePersistedFilters();
  const activeFiltersCount = getAppliedFilterCount(filterState);
  const { dateRange, setDateRange } = usePersistedDateRange();
  const [trendView, setTrendView] = useState("recovery_score");

  const filterParams = useMemo(
    () => buildFilterParams(filterState, dateRange, effectiveClientId),
    [filterState, dateRange, effectiveClientId]
  );

  // Client events: central admins and client admins may annotate; the popover
  // is read-only for everyone else. Only enabled for a single selected client.
  const { data: session } = useSessionQuery();
  const canEditEvents = Boolean(isCentralAdmin || session?.is_admin);
  const [startDate, endDate] = dateRange;
  const eventStartDate = startDate ? startDate.format("YYYY-MM-DD") : undefined;
  const eventEndDate = endDate ? endDate.format("YYYY-MM-DD") : undefined;

  const granularity = useMemo(
    () => calculateGranularity(dateRange),
    [dateRange]
  );

  const overallRangeQuery = useMetricsOverallRange(effectiveClientId);

  const overallMinMax = useMemo(() => {
    const metric_min = overallRangeQuery.data?.data?.metric_min ?? {};
    const metric_max = overallRangeQuery.data?.data?.metric_max ?? {};
    return {
      recovery_score: {
        min: typeof metric_min.recovery_score === "number" ? metric_min.recovery_score : undefined,
        max: typeof metric_max.recovery_score === "number" ? metric_max.recovery_score : undefined,
      },
      hrv: {
        min: typeof metric_min.hrv === "number" ? metric_min.hrv : undefined,
        max: typeof metric_max.hrv === "number" ? metric_max.hrv : undefined,
      },
      resting_heart_rate: {
        min: typeof metric_min.resting_heart_rate === "number" ? metric_min.resting_heart_rate : undefined,
        max: typeof metric_max.resting_heart_rate === "number" ? metric_max.resting_heart_rate : undefined,
      },
      sleep_performance: {
        min: typeof metric_min.sleep_performance === "number" ? metric_min.sleep_performance : undefined,
        max: typeof metric_max.sleep_performance === "number" ? metric_max.sleep_performance : undefined,
      },
      heart_rate: {
        min: typeof metric_min.heart_rate === "number" ? metric_min.heart_rate : undefined,
        max: typeof metric_max.heart_rate === "number" ? metric_max.heart_rate : undefined,
      },
    };
  }, [overallRangeQuery.data]);

  // Comparison mode: when 2+ values of a filter dimension are selected, the user
  // can switch from a single blended average to one column/line per value.
  const comparison = useComparisonMode(filterState);
  const { groups, isComparing, overflowCount } = useComparisonGroups(
    filterState,
    dateRange,
    effectiveClientId,
    comparison
  );

  const { buildPayload: buildAiPayload, disabled: aiDisabled } = useAiSummary({
    dashboard: "Recovery",
    groups,
    isComparing,
    dimension: comparison.dimension,
    filterParams,
    metricSpecs: [
      { label: "Recovery", keys: ["recovery_score", "recovery"] },
      { label: "Resting heart rate", keys: ["resting_heart_rate"] },
      { label: "HRV", keys: ["hrv", "average_hrv"] },
      { label: "Sleep performance", keys: ["sleep_performance"] },
    ],
  });

  // Cohort count for privacy guard (page-level; columns guard themselves too).
  const cohortQuery = useMetricsStats({ ...filterParams, metrics: ["active_users"] });
  const activeUserCount = cohortQuery.data?.data?.active_users?.value;
  const trendsQuery = useRecoveryTrends({
    ...filterParams,
    metrics: [
      "recovery_score",
      "resting_heart_rate",
      "hrv",
      "sleep_performance",
    ],
    granularity,
  });

  // Per-group trend data for the overlay (only meaningful in comparison mode).
  const comparisonTrends = useComparisonTrends(
    groups,
    ["recovery_score", "resting_heart_rate", "hrv", "sleep_performance"],
    granularity
  );

  const patternsQuery = useHeartRatePatterns({
    ...filterParams,
    metric: "heart_rate_patterns",
    granularity: "hourly",
  });

  // Per-group heart-rate patterns for the overlay (comparison mode only).
  const comparisonPatterns = useComparisonPatterns(
    groups,
    "heart_rate_patterns",
    "hourly"
  );

  const handleApplyFilters = (filters: FilterState) => {
    setFilterState(filters);
    setIsFilterOpen(false);
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
  };

  const trendsData = useMemo(() => {
    const transformed = transformTrendsData(trendsQuery.data, [
      "recovery_score",
      "resting_heart_rate",
      "hrv",
      "sleep_performance",
    ]);
    return transformed.map((item) => {
      const cleaned: Record<string, string | number> = {};
      Object.keys(item).forEach((key) => {
        const value = item[key];
        if (value !== null) {
          cleaned[key] = value;
        }
      });
      return cleaned;
    });
  }, [trendsQuery.data]);

  // In comparison mode, overlay one line per group for the selected trend metric.
  const comparisonTrendChart = useMemo(() => {
    const groupInputs = comparisonTrends.map((entry) => ({
      label: entry.group.label,
      color: entry.group.color,
      data: transformTrendsData(entry.data, [trendView]),
    }));
    return buildComparisonTrendSeries(groupInputs, trendView, "label");
  }, [comparisonTrends, trendView]);

  const comparisonTrendsLoading = comparisonTrends.some((t) => t.isLoading);

  const patternsData = useMemo(() => {
    if (!patternsQuery.data?.data?.patterns) return [];
    return patternsQuery.data.data.patterns
      .filter((pattern) => pattern.time)
      .map((pattern) => ({
        time: pattern.time!,
        value: pattern.value,
        value_min: pattern.value_min ?? null,
        value_max: pattern.value_max ?? null,
      }));
  }, [patternsQuery.data]);

  // In comparison mode, overlay one heart-rate line per group across the day.
  const comparisonPatternChart = useMemo(() => {
    const groupInputs = comparisonPatterns.map((entry) => ({
      label: entry.group.label,
      color: entry.group.color,
      data: entry.data?.data?.patterns ?? [],
    }));
    return buildComparisonPatternSeries(groupInputs, "time");
  }, [comparisonPatterns]);

  const comparisonPatternsLoading = comparisonPatterns.some((p) => p.isLoading);

  const getTrendLabel = () => {
    switch (trendView) {
      case "recovery_score":
        return "Recovery Score";
      case "hrv":
        return "Heart Rate Variability";
      case "resting_heart_rate":
        return "Resting Heart Rate";
      case "sleep_performance":
        return "Sleep Performance";
      default:
        return "Recovery Score";
    }
  };

  const getYAxisMax = () => {
    switch (trendView) {
      case "recovery_score":
      case "sleep_performance":
        return 100;
      case "hrv":
        return 200;
      case "resting_heart_rate":
        return 100;
      default:
        return 100;
    }
  };

  const isLoading = trendsQuery.isLoading || patternsQuery.isLoading;

  const hasError = trendsQuery.isError || patternsQuery.isError;

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <ComparisonToggle comparison={comparison} />
            <DurationSelector
              value={dateRange}
              onChange={handleDateRangeChange}
              buttonVariant="outlined"
            />
            <Button
              variant="text"
              size="medium"
              startIcon={<FilterListIcon />}
              color={activeFiltersCount > 0 ? "primary" : "inherit"}
              onClick={() => setIsFilterOpen(true)}
            >
              {activeFiltersCount > 0 ? `FILTER (${activeFiltersCount})` : "FILTER"}
            </Button>
          </Box>
        </Box>

        <FilterDrawer
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={handleApplyFilters}
          initialFilters={filterState}
        />

        {hasError && (
          <Alert severity="error">
            Failed to load recovery data. Please try again later.
          </Alert>
        )}

        {/* Stat cards + recovery distribution, one column per comparison group
            (or a single full-width column in average mode). */}
        <ComparisonColumns groups={groups} overflowCount={overflowCount}>
          {(group) => (
            <RecoveryStatsColumn
              filterParams={group.filterParams}
              isCentralAdmin={isCentralAdmin}
              isAnonymised={isAnonymised}
              stacked={isComparing}
            />
          )}
        </ComparisonColumns>

        <DashboardCard title="AI Summary" tooltip="AI narrative of the metrics shown." expandable={false}>
          <AiSummaryPanel
            title={isComparing ? "Compare these groups with AI" : "Summarise this view with AI"}
            buildPayload={buildAiPayload}
            disabled={aiDisabled}
          />
        </DashboardCard>

        <WeatherAnomalyCallout filters={filterParams} />

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title="Recovery Trends"
              subtitle="Daily trends showing recovery metrics over time."
              metricKey={trendView}
              details={RECOVERY_DETAILS.recoveryTrends}
              titleComponent={
                <ToggleButtonGroup
                  value={trendView}
                  exclusive
                  onChange={(_, newValue) => {
                    if (newValue !== null) setTrendView(newValue);
                  }}
                  size="small"
                >
                  <ToggleButton value="recovery_score">
                    Recovery Score
                  </ToggleButton>
                  <ToggleButton value="hrv">
                    Heart Rate Variability
                  </ToggleButton>
                  <ToggleButton value="resting_heart_rate">
                    Resting Heart Rate
                  </ToggleButton>
                  <ToggleButton value="sleep_performance">
                    Sleep Performance
                  </ToggleButton>
                </ToggleButtonGroup>
              }
            >
              <CohortGuard
                userCount={activeUserCount}
                bypass={isCentralAdmin || !isAnonymised}
                loading={cohortQuery.isLoading}
              >
                {(isComparing ? comparisonTrendsLoading : isLoading) ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : isComparing ? (
                  comparisonTrendChart.data.length > 0 ? (
                    <LineChart
                      data={comparisonTrendChart.data}
                      xAxisKey="label"
                      series={comparisonTrendChart.series}
                      height={300}
                      yAxisMin={0}
                      yAxisMax={getYAxisMax()}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: 300,
                      }}
                    >
                      <Alert severity="info">No trend data available</Alert>
                    </Box>
                  )
                ) : trendsData.length > 0 ? (
                  <AnnotatableChart
                    clientId={effectiveClientId}
                    startDate={eventStartDate}
                    endDate={eventEndDate}
                    weatherFilters={filterParams}
                    canEdit={canEditEvents}
                    data={trendsData}
                    xAxisKey="label"
                    series={[
                      {
                        dataKey: trendView,
                        label: getTrendLabel(),
                        color: theme.palette.success.main,
                        area: true,
                      },
                    ]}
                    height={300}
                    yAxisMin={0}
                    yAxisMax={getYAxisMax()}
                    showMinMaxLines
                    referenceMin={overallMinMax[trendView as keyof typeof overallMinMax]?.min}
                    referenceMax={overallMinMax[trendView as keyof typeof overallMinMax]?.max}
                  />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <Alert severity="info">No trend data available</Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>
        </Grid>

        {/* Recovery Score Distribution renders inside each comparison column
            (RecoveryStatsColumn). Heart-rate patterns overlay one line per group
            in comparison mode, or a single line in average mode. */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title="Heart Rate Patterns by Time of Day"
              subtitle="Variation in heart rate patterns throughout the day, highlighting peak and low points."
              metricKey="heart_rate"
              details={RECOVERY_DETAILS.heartRatePatternsByTimeOfDay}
            >
              <CohortGuard
                userCount={activeUserCount}
                bypass={isCentralAdmin || !isAnonymised}
                loading={cohortQuery.isLoading}
              >
                {(isComparing ? comparisonPatternsLoading : isLoading) ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : isComparing ? (
                  comparisonPatternChart.data.length > 0 ? (
                    <LineChart
                      data={comparisonPatternChart.data}
                      xAxisKey="time"
                      series={comparisonPatternChart.series}
                      height={300}
                      yAxisMin={0}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: 300,
                      }}
                    >
                      <Alert severity="info">No pattern data available</Alert>
                    </Box>
                  )
                ) : patternsData.length > 0 ? (
                  <LineChart
                    data={patternsData}
                    xAxisKey="time"
                    series={[
                      {
                        dataKey: "value",
                        label: "Heart Rate",
                        color: theme.palette.warning.main,
                        area: true,
                      },
                    ]}
                    height={300}
                    yAxisMin={0}
                    showMinMaxLines
                  />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <Alert severity="info">No pattern data available</Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
